import { memo, useContext, useCallback } from 'react';
import Image from '@next/image';
import classNames from 'classnames';

import { ProductContext } from '@providers/ProductProvider';
import { ConfigContext } from '@providers/ConfigProvider';
import Link from '@components/atoms/Link';
import Checkbox from '@components/atoms/Checkbox';
import Typography from '@components/atoms/Typography';
import Amount from '@templates/Amount';
import HtmlPreview from '@components/HtmlPreview';
import { FormattedMessage, useIntl } from 'utils/intl';
import getPlainText from 'utils/getPlainText';
import getUniqueFlatArray from 'utils/getUniqueFlatArray';

import classes from './ComplementaryProducts.module.scss';
import messages from './ComplementaryProducts.messages';

const ComplementaryProducts = (): JSX.Element => {
  const intl = useIntl();
  const { hasIndividualCustomers } = useContext(ConfigContext);
  const { complementaryVariants, selectedComplementaryProductsIds, setSelectedComplementaryProductsIds } =
    useContext(ProductContext);

  const onChange = useCallback(
    (selectedId: number, value: boolean) =>
      setSelectedComplementaryProductsIds((complementaryProductsIds) =>
        value
          ? getUniqueFlatArray([...complementaryProductsIds, selectedId])
          : complementaryProductsIds.filter((complementaryProductId) => complementaryProductId !== selectedId)
      ),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      <Typography variant="body2" className={classes.name}>
        <FormattedMessage {...messages.complementaryProducts} />
      </Typography>
      <div className={classes.wrapper}>
        {complementaryVariants.map((complementaryVariant) => {
          const { id, name, productName, grossPrice, priceRrp, thumbnail, href } = complementaryVariant;
          const isActive = selectedComplementaryProductsIds.includes(id);

          return (
            <div
              key={id}
              className={classNames(classes.complementaryProduct, {
                [classes.active]: isActive,
              })}
            >
              <Checkbox
                id={`complementary-item-${id}`}
                checked={isActive}
                classes={{ root: classes.checkbox }}
                onChange={() => onChange(id, !isActive)}
              />
              <div className={classes.label}>
                <Link component="span" className={classes.link} href={href} scroll>
                  <div className={classes.imageWrapper}>
                    <Image
                      layout="fill"
                      objectFit="contain"
                      alt={intl.formatMessage(messages.productThumbnail)}
                      resolveConfig={{
                        preset: 'p_thumb_5',
                        description: 'product-thumbnail',
                        productName,
                        extension: thumbnail.extension,
                        name: thumbnail.basename,
                        width: thumbnail.width,
                        height: thumbnail.height,
                      }}
                      hasLazyLoad={false}
                    />
                  </div>
                  <Typography variant="caption1" ellipsisLines={2} title={getPlainText(name)} className={classes.name}>
                    <HtmlPreview htmlText={name} />
                  </Typography>
                </Link>
                <div className={classes.price}>
                  {priceRrp && priceRrp.amount > grossPrice.amount && !hasIndividualCustomers && (
                    <Amount size="micro" className={classes.priceRrp} price={priceRrp} />
                  )}
                  <Amount size="little" price={grossPrice} options={{ signDisplay: 'always' }} isBolded />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(ComplementaryProducts);
